import { useEffect, useRef } from 'react'
import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { useLazyLoyaltyPoints } from '../../graphQL/queries/loyaltyPoints'
import type { ILoyaltyPointsResponse } from '../../graphQL/queries/loyaltyPoints/types'
import { setLoyaltyPointsToEarn } from '../../redux/slices/config/config'
import { generateDataDogContext } from '../../utils/dataDog'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'

const FUNCTION_NAME = 'useLoyaltyPointsPreflight'
const DEBOUNCE_MS = 300

/**
 * Asks the backend how many points the shopper will earn on the current
 * cart, writes the number into `defaultSlice.loyaltyPointsToEarn`, and
 * hides the widget by writing `null` on an empty answer or an error
 * (COM-1397).
 *
 * Mounted by `templates/Cart.tsx` next to `useCheckoutRulePreflight` — same
 * reasoning: this is the one screen where the number is actionable, and
 * asking on every container would be one request per navigation for an
 * answer no other screen reads.
 *
 * **Three states collapse to `null` in the slice.** The backend's own
 * `null`/`0` (empty cart, nothing to earn), and any error — an unopted-in
 * storefront answers "loyaltyPoints is not implemented. It must be setup as
 * an add-on" (COM-1398). The widget's own render guard hides on `null`,
 * `undefined`, or `0`, so collapsing here keeps a single intent flowing
 * end-to-end.
 *
 * **Why debounce.** The effect re-fires on every cart edit — add, remove,
 * clear, quantity change — through `cartSignature`. A shopper tapping `+`
 * five times fires five mutations and five settled responses; without a
 * debounce that is five loyalty-points fetches on top. 300ms is the same
 * window `src/utils/debounce.ts` ships as its default and the same one
 * `useAddItemToCart` uses for its cart writes, so the loyalty-points fetch
 * settles one tick after the cart write settles.
 *
 * **Why silent on error.** Mirrors `useCheckoutRulePreflight`: nothing on
 * the cart waits on this answer, so an alert over a working cart would be
 * worse than silence. The RUM log keeps the add-on misconfigurations and
 * transport failures visible to the Commerce team.
 *
 * **No ref, no mount-only guard.** Same reasoning the sibling preflight
 * records: `index.tsx` mounts through the legacy `ReactDOM.render`, so
 * React 18's StrictMode double-mount is a test-only concern. A second
 * fetch is harmless because it only re-writes the same slice slot.
 */
export const useLoyaltyPointsPreflight = () => {
  const dispatch = useAppDispatch()
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  /*
   * Clear-on-mount guard. The slice value is kept across cart entries, so a
   * shopper who leaves the cart with a 200-point pill and returns would
   * otherwise see 200 stuck on screen through the 300 ms debounce and the
   * fetch — the opposite of the documented "show nothing while loading"
   * state. On the first run of the effect for a given mount we dispatch
   * `null` to erase whatever the prior mount left behind, then let the
   * normal entry fetch write the fresh number. Cart edits on the same
   * mount keep the previous number in place — the ref gates us to the
   * entry run only, so the pill does not flicker between a `+` and a `-`.
   */
  const didMountRef = useRef(false)
  /*
   * Cart content signature (COM-1213 pattern). A primitive the server-side
   * answer could move with — totals and item count — so an edit that
   * settles with a changed cart re-asks. `useAppSelector` compares by
   * `===`, which pins stability as long as the four fields do.
   */
  const cartSignature = useAppSelector(
    (state) =>
      `${state.cartSlice?.total ?? 0}|${state.cartSlice?.itemsTotal ?? 0}|${
        state.cartSlice?.items?.length ?? 0
      }`,
  )

  const onCompleted = (data: ILoyaltyPointsResponse) => {
    const points = data?.loyaltyPoints?.pointsToEarn
    dispatch(
      setLoyaltyPointsToEarn(
        typeof points === 'number' && points > 0 ? points : null,
      ),
    )
  }

  const onError = (error: ApolloError) => {
    /*
     * Silent on error. The shopper's cart is working; a loyalty-points
     * failure never blocks a checkout. Write `null` so a prior
     * successful number does not linger over a freshly-failed cart.
     * Record enough for Commerce to see the pattern on the Datadog side.
     */
    dispatch(setLoyaltyPointsToEarn(null))
    datadogRum.addError(error, {
      ...generateDataDogContext({
        title: 'loyaltyPoints query failed',
        extraInfo: {
          function: FUNCTION_NAME,
          graphQLErrors: error.graphQLErrors?.map((e) => e.message),
          networkError: error.networkError?.message,
        },
      }),
    })
  }

  const [fetchLoyaltyPoints] = useLazyLoyaltyPoints({ onCompleted, onError })

  /*
   * Fire on mount and whenever the cart changes, debounced in-effect with
   * a bare `setTimeout` so the debounce window shares the effect's own
   * cleanup. A `useMemo`-wrapped debouncer would need its own cleanup for
   * the trailing timer on unmount; the effect cleanup covers it with no
   * extra ceremony.
   *
   * ⚠️ **`sessionId` is a guard as well as a dependency.** `Config`
   * dispatches it from an effect that runs children-first, so on the
   * first render here it is still `undefined`. Apollo drops an
   * `undefined` ID variable from the request body; `''` reaches the
   * server and the server rejects it. The guard keeps the first render
   * inert and lets the dependency re-fire once the session arrives.
   */
  useEffect(() => {
    if (sessionId === undefined) return undefined
    if (!didMountRef.current) {
      didMountRef.current = true
      dispatch(setLoyaltyPointsToEarn(null))
    }
    const timer = setTimeout(() => {
      fetchLoyaltyPoints({
        variables: {
          loyaltyPointsStorefrontName: storeName || '',
          loyaltyPointsSessionUid: sessionId,
        },
      })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, storeName, cartSignature])
}

export default useLoyaltyPointsPreflight
